// Client-side mirror of server movement/battle logic for UI preview.
// Server always validates; this is for highlighting valid actions only.
import type { HexCoord, Unit, TerrainHex, CommandCard, BridgeGroup } from '~shared/types';
import { getVariantEffect } from '~shared/unit-variants';
import { getTerrainRule, TERRAIN_RULES, getCombinedTerrainEffect } from '~shared/terrain-rules';

const BOARD_ROWS = 9;
function maxColInRow(row: number): number { return row % 2 === 0 ? 12 : 11; }
function isValidHexClient(col: number, row: number): boolean {
  return row >= 0 && row < BOARD_ROWS && col >= 0 && col <= maxColInRow(row);
}

export function maxMoveDistance(type: Unit['type']): number {
  return type === 'infantry' ? 2 : type === 'armor' ? 3 : 1;
}

export function maxAttackRange(type: Unit['type']): number {
  if (type === 'artillery') return 6;
  return 3;
}

function getHexAt(col: number, row: number, terrain: TerrainHex[]): TerrainHex {
  return terrain.find(t => t.col === col && t.row === row) ?? { col, row, type: 'clear' };
}

function effectAt(hex: TerrainHex) {
  return getCombinedTerrainEffect(hex.type, hex.overlay);
}

const EVEN_DIRS = [
  { dc: 0, dr: -1 }, { dc: 1, dr: 0 }, { dc: 0, dr: 1 },
  { dc: -1, dr: 1 }, { dc: -1, dr: 0 }, { dc: -1, dr: -1 },
];
const ODD_DIRS = [
  { dc: 1, dr: -1 }, { dc: 1, dr: 0 }, { dc: 1, dr: 1 },
  { dc: 0, dr: 1 },  { dc: -1, dr: 0 }, { dc: 0, dr: -1 },
];

function neighbors(h: HexCoord): HexCoord[] {
  const dirs = h.row % 2 === 0 ? EVEN_DIRS : ODD_DIRS;
  return dirs
    .map(d => ({ col: h.col + d.dc, row: h.row + d.dr }))
    .filter(n => isValidHexClient(n.col, n.row));
}

function toCube(h: HexCoord) {
  const x = h.col - (h.row - (h.row & 1)) / 2;
  const z = h.row;
  return { x, y: -x - z, z };
}

function cubeRound(x: number, y: number, z: number): { x: number; y: number; z: number } {
  let rx = Math.round(x), ry = Math.round(y), rz = Math.round(z);
  const dx = Math.abs(rx - x), dy = Math.abs(ry - y), dz = Math.abs(rz - z);
  if      (dx > dy && dx > dz) rx = -ry - rz;
  else if (dy > dz)            ry = -rx - rz;
  else                         rz = -rx - ry;
  return { x: rx, y: ry, z: rz };
}

function cubeToOffset(cx: number, cz: number): HexCoord {
  return { col: cx + (cz - (cz & 1)) / 2, row: cz };
}

function isHexBlockingLoS(
  col: number, row: number,
  unitKeys: ReadonlySet<string>,
  terrain: TerrainHex[],
  attackerOnHill: boolean,
  targetOnHill: boolean,
): boolean {
  const hex = getHexAt(col, row, terrain);
  if (hex.type === 'hill') return !(attackerOnHill && targetOnHill); // plateau: both on hills → transparent
  return effectAt(hex).blocksLoS || unitKeys.has(`${col},${row}`);
}

// Scenario special rule ('artillery-no-hill-los'): artillery normally ignores LoS entirely,
// but some scenarios block it specifically by hills. Only hill / impassable-hill hexes block
// under this mode; all other terrain and units remain transparent.
function isHillBlockingArtilleryLoS(
  col: number, row: number,
  attackerOnHill: boolean, targetOnHill: boolean,
  terrain: TerrainHex[],
): boolean {
  const hex = getHexAt(col, row, terrain);
  if (hex.type === 'hill') return !(attackerOnHill && targetOnHill);
  return hex.type === 'impassable-hill';
}

function pathCrossesRiver(from: HexCoord, to: HexCoord, terrain: TerrainHex[]): boolean {
  const dist = hexDistance(from, to);
  if (dist <= 1) return false;
  const ac = toCube(from);
  const tc = toCube(to);
  const EPS = 1e-6;
  for (let i = 1; i < dist; i++) {
    const t = i / dist;
    const fx = ac.x + (tc.x - ac.x) * t;
    const fy = ac.y + (tc.y - ac.y) * t;
    const fz = ac.z + (tc.z - ac.z) * t;
    const r1 = cubeRound(fx + EPS, fy + 2 * EPS, fz - 3 * EPS);
    const r2 = cubeRound(fx - EPS, fy - 2 * EPS, fz + 3 * EPS);
    const h1 = cubeToOffset(r1.x, r1.z);
    const h2 = cubeToOffset(r2.x, r2.z);
    const isRiver = (h: HexCoord) => getHexAt(h.col, h.row, terrain).type === 'river';
    if (h1.col === h2.col && h1.row === h2.row) {
      if (isRiver(h1)) return true;
    } else {
      if (isRiver(h1) && isRiver(h2)) return true;
    }
  }
  return false;
}

function hasLineOfSight(
  attacker: HexCoord,
  target: HexCoord,
  attackerType: Unit['type'],
  allUnits: Unit[],
  terrain: TerrainHex[],
  artilleryBlockedByHills = false,
): boolean {
  const artilleryHillCheck = attackerType === 'artillery' && artilleryBlockedByHills;
  if (attackerType === 'artillery' && !artilleryBlockedByHills) return true;
  const dist = hexDistance(attacker, target);
  if (dist <= 1) return true;

  const ac = toCube(attacker);
  const tc = toCube(target);
  const unitKeys = new Set(
    allUnits
      .filter(u => !(u.col === attacker.col && u.row === attacker.row))
      .map(u => `${u.col},${u.row}`),
  );

  const attackerOnHill = getHexAt(attacker.col, attacker.row, terrain).type === 'hill';
  const targetOnHill   = getHexAt(target.col,   target.row,   terrain).type === 'hill';

  const blocks = (col: number, row: number): boolean => artilleryHillCheck
    ? isHillBlockingArtilleryLoS(col, row, attackerOnHill, targetOnHill, terrain)
    : isHexBlockingLoS(col, row, unitKeys, terrain, attackerOnHill, targetOnHill);

  const EPS = 1e-6;
  for (let i = 1; i < dist; i++) {
    const t = i / dist;
    const fx = ac.x + (tc.x - ac.x) * t;
    const fy = ac.y + (tc.y - ac.y) * t;
    const fz = ac.z + (tc.z - ac.z) * t;

    const r1 = cubeRound(fx + EPS, fy + 2 * EPS, fz - 3 * EPS);
    const r2 = cubeRound(fx - EPS, fy - 2 * EPS, fz + 3 * EPS);
    const h1 = cubeToOffset(r1.x, r1.z);
    const h2 = cubeToOffset(r2.x, r2.z);

    if (h1.col === h2.col && h1.row === h2.row) {
      if (blocks(h1.col, h1.row)) return false;
    } else {
      if (blocks(h1.col, h1.row) && blocks(h2.col, h2.row)) return false;
    }
  }
  return true;
}

export function hexDistance(a: HexCoord, b: HexCoord): number {
  const ac = toCube(a); const bc = toCube(b);
  return Math.max(Math.abs(ac.x - bc.x), Math.abs(ac.y - bc.y), Math.abs(ac.z - bc.z));
}

/** Whether a unit is eligible to battle given how far it moved and its current terrain. */
export function canBattle(unit: Unit, terrain?: TerrainHex[], moveBonusHexes = 0, maxAttacks = 1): boolean {
  if ((unit.attackCount ?? 0) >= maxAttacks) return false;
  if (terrain) {
    const hex  = getHexAt(unit.col, unit.row, terrain);
    const rule = effectAt(hex);
    if (rule.noBattleFromHexTypes?.includes(unit.type)) return false;
    const blocksThisType = !rule.blocksBattleAfterMoveTypes || rule.blocksBattleAfterMoveTypes.includes(unit.type);
    if (unit.hexesMoved > 0 && rule.blocksBattleAfterMove && blocksThisType) {
      // French Resistance: a 1-hex move into terrain that normally blocks
      // post-move battling (forest/town/hedgerow) is still allowed to battle.
      const ignoreMaxHexes = getVariantEffect(unit.variant)?.ignoreBlocksBattleAfterMoveMaxHexes;
      if (ignoreMaxHexes === undefined || unit.hexesMoved > ignoreMaxHexes) return false;
    }
    if (unit.leftTerrainType && getTerrainRule(unit.leftTerrainType).blocksBattleAfterLeavingTypes?.includes(unit.type)) {
      return false;
    }
    if (rule.battleAfterMoveOk?.includes(unit.type)) return true;
  }
  switch (unit.type) {
    case 'infantry': {
      const limit = (getVariantEffect(unit.variant)?.canBattleAfterMoveHexes ?? 1) + moveBonusHexes;
      return unit.hexesMoved <= limit;
    }
    case 'armor':     return true;
    case 'artillery': return unit.hexesMoved === 0;
  }
}

export function getValidMoveHexes(
  unit: Unit, allUnits: Unit[], terrain: TerrainHex[],
  maxDistOverride?: number, ignoreTerrainRestrictions = false,
  hillMovementCost = false,
  bridgeGroups?: BridgeGroup[],
): HexCoord[] {
  if (unit.hexesMoved > 0) return [];
  const startHex  = getHexAt(unit.col, unit.row, terrain);
  const startRule = effectAt(startHex);
  if (startRule.pinnedTypes?.includes(unit.type)) return [];
  const leavingLimit = !ignoreTerrainRestrictions ? startRule.leavingMoveLimit ?? null : null;
  const maxDist = leavingLimit ?? (maxDistOverride ?? maxMoveDistance(unit.type));
  const occupied = new Set(allUnits.map(u => `${u.col},${u.row}`));
  const reachable: HexCoord[] = [];
  const visited = new Map<string, number>();
  const bridgeChecks = (bridgeGroups ?? []).map(g => ({
    bridgeSet: new Set(g.bridgeHexes.map(h => `${h.col},${h.row}`)),
    entrySet:  new Set(g.entryExitHexes.map(h => `${h.col},${h.row}`)),
  }));
  const queue: Array<{ coord: HexCoord; dist: number; from?: HexCoord }> = [
    { coord: { col: unit.col, row: unit.row }, dist: 0 },
  ];
  visited.set(`${unit.col},${unit.row}`, 0);

  while (queue.length > 0) {
    // Dijkstra: pop minimum-cost entry (handles variable step costs correctly)
    let minIdx = 0;
    for (let i = 1; i < queue.length; i++) {
      if (queue[i].dist < queue[minIdx].dist) minIdx = i;
    }
    const { coord, dist, from } = queue.splice(minIdx, 1)[0];
    const coordKey = `${coord.col},${coord.row}`;

    // Skip stale entries (a cheaper path to this coord was already found)
    if (dist > (visited.get(coordKey) ?? Infinity)) continue;

    const coordHex = getHexAt(coord.col, coord.row, terrain);

    if (dist > 0) {
      const rule = effectAt(coordHex);
      if (rule.impassableTo.includes(unit.type)) continue;
      if (occupied.has(coordKey)) continue;
      if (!ignoreTerrainRestrictions) {
        if (rule.maxMoveDistIfEntering !== undefined && dist > rule.maxMoveDistIfEntering) continue;

        // Directional cliff/bluff rule: entering hill or bluff FROM beach costs all movement.
        // Bluffs additionally block armor from climbing the beach face entirely.
        if ((coordHex.type === 'hill' || coordHex.type === 'bluff') && from) {
          const fromHex = getHexAt(from.col, from.row, terrain);
          if (fromHex.type === 'beach') {
            if (coordHex.type === 'bluff' && unit.type === 'armor') continue;
            if (dist > 1) continue;
            reachable.push(coord); continue;
          }
        }

        // Minefield forces a stop on entry independent of the base terrain (no unit-type exemption).
        if ((rule.stopsMovement && !rule.moveExempt.includes(unit.type)) || coordHex.minefield) { reachable.push(coord); continue; }
      }
      reachable.push(coord);
    }
    if (dist >= maxDist) continue;
    for (const nb of neighbors(coord)) {
      const key   = `${nb.col},${nb.row}`;
      const nbHex = getHexAt(nb.col, nb.row, terrain);
      if (!ignoreTerrainRestrictions && nbHex.type === 'hedgerow' && dist > 0) continue;
      // Bridge entry/exit restriction
      let bridgeBlocked = false;
      for (const { bridgeSet, entrySet } of bridgeChecks) {
        const nbOnBridge  = bridgeSet.has(key);
        const srcOnBridge = bridgeSet.has(coordKey);
        if (nbOnBridge && !srcOnBridge && !entrySet.has(coordKey)) { bridgeBlocked = true; break; }
        if (srcOnBridge && !nbOnBridge && !entrySet.has(key))      { bridgeBlocked = true; break; }
      }
      if (bridgeBlocked) continue;
      // Hill movement cost: climbing onto a hill from flat terrain costs 2, all other moves cost 1
      const stepCost = hillMovementCost && coordHex.type !== 'hill' && nbHex.type === 'hill' ? 2 : 1;
      const newDist  = dist + stepCost;
      if (newDist > maxDist) continue; // over budget — never add to reachable
      if ((visited.get(key) ?? Infinity) > newDist) {
        visited.set(key, newDist);
        queue.push({ coord: nb, dist: newDist, from: coord });
      }
    }
  }
  return reachable;
}

export function getValidAttackTargets(
  attacker: Unit, allUnits: Unit[], terrain: TerrainHex[],
  activeCard?: CommandCard | null, maxAttacksOverride?: number,
  riverBlocksInfantryBattle?: boolean,
  artilleryBlockedByHills?: boolean,
): Unit[] {
  const moveBonus = cardMoveBonusFor(attacker, activeCard);
  const maxAtks = maxAttacksOverride ?? (
    (activeCard?.effects?.specialAction === 'artillery-bombard'
      && attacker.type === 'artillery' && attacker.hexesMoved === 0) ? 2 : 1
  );
  if (!canBattle(attacker, terrain, moveBonus, maxAtks)) return [];
  const range = maxAttackRange(attacker.type);
  const pos = { col: attacker.col, row: attacker.row };
  const validTargets = allUnits.filter(u => {
    if (u.side === attacker.side) return false;
    const tPos = { col: u.col, row: u.row };
    const dist = hexDistance(pos, tPos);
    if (dist > range) return false;
    if (!hasLineOfSight(pos, tPos, attacker.type, allUnits, terrain, artilleryBlockedByHills)) return false;
    if (riverBlocksInfantryBattle && attacker.type === 'infantry' && pathCrossesRiver(pos, tPos, terrain)) return false;
    // Exclude targets where terrain penalties would reduce dice to 0 (nothing to roll)
    if (attackDiceCount(attacker, u, terrain, activeCard) === 0) return false;
    return true;
  });
  // Close combat: if any valid target is adjacent, the attacker may only target adjacent enemies
  const adjacent = validTargets.filter(u => hexDistance(pos, { col: u.col, row: u.row }) === 1);
  return adjacent.length > 0 ? adjacent : validTargets;
}

export function cardMoveBonusFor(unit: Unit, card?: CommandCard | null): number {
  const fx = card?.effects;
  if (!fx?.movementBonus) return 0;
  if (fx.unitTypeFilter && unit.type !== fx.unitTypeFilter) return 0;
  return fx.movementBonus;
}

function cardCombatBonus(attacker: Unit, distance: number, card?: CommandCard | null): number {
  const fx = card?.effects;
  if (!fx?.combatBonusDice) return 0;
  switch (fx.combatCondition) {
    case 'did-not-move': return attacker.hexesMoved === 0 ? fx.combatBonusDice : 0;
    case 'adjacent':     return distance === 1 && attacker.type === 'infantry' ? fx.combatBonusDice : 0;
    default:             return fx.combatBonusDice;
  }
}

export function baseDiceCount(type: Unit['type'], distance: number): number {
  switch (type) {
    case 'infantry':  return ([0, 3, 2, 1])[distance] ?? 0;
    case 'armor':     return distance >= 1 && distance <= 3 ? 3 : 0;
    case 'artillery': return ([0, 3, 3, 2, 2, 1, 1])[distance] ?? 0;
  }
}

function terrainDiceModifier(atkHex: TerrainHex, defHex: TerrainHex, attackerType: Unit['type']): number {
  const baseDefRule    = getTerrainRule(defHex.type);
  const overlayDefRule = defHex.overlay ? TERRAIN_RULES[defHex.overlay] : null;

  let baseBonus    = baseDefRule.defenseBonus[attackerType] ?? 0;
  let overlayBonus = overlayDefRule ? (overlayDefRule.defenseBonus[attackerType] ?? 0) : 0;

  // Hill/bluff plateau: no bonus when both are at the same elevated terrain level.
  if ((defHex.type === 'hill' || defHex.type === 'bluff') &&
      (atkHex.type === 'hill' || atkHex.type === 'bluff')) baseBonus = 0;

  const defenseBonus = Math.max(baseBonus, overlayBonus);

  const atkRule      = effectAt(atkHex);
  const attackPenalty = atkRule.attackPenalty[attackerType] ?? 0;

  return -(defenseBonus + attackPenalty);
}

/** Dice the attacker would roll against a specific target, including terrain and card modifiers. */
export function attackDiceCount(attacker: Unit, target: Unit, terrain: TerrainHex[], activeCard?: CommandCard | null): number {
  const dist = hexDistance(
    { col: attacker.col, row: attacker.row },
    { col: target.col,   row: target.row   },
  );
  const base    = baseDiceCount(attacker.type, dist);
  const atkHex  = getHexAt(attacker.col, attacker.row, terrain);
  const defHex  = getHexAt(target.col,   target.row,   terrain);
  const mod     = terrainDiceModifier(atkHex, defHex, attacker.type) + cardCombatBonus(attacker, dist, activeCard);
  return Math.max(0, base + mod);
}

export interface DiceBreakdown {
  lines: string[];
  hasReductions: boolean;
}

/** Breakdown of dice reductions for display in the battle panel. */
export function attackDiceBreakdown(attacker: Unit, target: Unit, terrain: TerrainHex[], activeCard?: CommandCard | null): DiceBreakdown {
  const dist = hexDistance(
    { col: attacker.col, row: attacker.row },
    { col: target.col,   row: target.row   },
  );
  const fullBase    = baseDiceCount(attacker.type, 1);
  const rangeBase   = baseDiceCount(attacker.type, dist);
  const rangePenalty = fullBase - rangeBase;

  const atkHex = getHexAt(attacker.col, attacker.row, terrain);
  const defHex = getHexAt(target.col,   target.row,   terrain);

  const baseDefRule    = getTerrainRule(defHex.type);
  const overlayDefRule = defHex.overlay ? TERRAIN_RULES[defHex.overlay] : null;
  let baseBonus    = baseDefRule.defenseBonus[attacker.type] ?? 0;
  let overlayBonus = overlayDefRule ? (overlayDefRule.defenseBonus[attacker.type] ?? 0) : 0;
  if ((defHex.type === 'hill' || defHex.type === 'bluff') &&
      (atkHex.type === 'hill' || atkHex.type === 'bluff')) baseBonus = 0;
  const defPenalty = Math.max(baseBonus, overlayBonus);

  const atkRule    = effectAt(atkHex);
  const atkPenalty = atkRule.attackPenalty[attacker.type] ?? 0;
  const cardBonus  = cardCombatBonus(attacker, dist, activeCard);

  const hasReductions = rangePenalty > 0 || defPenalty > 0 || atkPenalty > 0 || cardBonus > 0;
  const defLabel = defHex.overlay ? `${defHex.overlay} on ${defHex.type}` : defHex.type;
  const atkLabel = atkHex.overlay ? `${atkHex.overlay} on ${atkHex.type}` : atkHex.type;

  const lines: string[] = [];
  if (hasReductions) {
    lines.push(`${fullBase} base`);
    if (rangePenalty > 0) lines.push(`−${rangePenalty} range ${dist}`);
    if (defPenalty   > 0) lines.push(`−${defPenalty} battle into ${defLabel}`);
    if (atkPenalty   > 0) lines.push(`−${atkPenalty} battle out of ${atkLabel}`);
    if (cardBonus    > 0) lines.push(`+${cardBonus} ${activeCard!.name}`);
  }
  return { lines, hasReductions };
}
